import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { stripeService } from '../../services/StripeService';

/**
 * RequireSubscription - Route guard de AUTENTICAÇÃO das rotas do app.
 *
 * MODELO DE CRÉDITOS (2026-08): o plano Free agora TEM acesso ao app —
 * ele recebe 2 créditos/mês (2 imagens) e o backend bloqueia com 403
 * quando o saldo acaba. Por isso este guard NÃO redireciona mais o Free
 * para /plans; só exige login. Admin continua protegido pelas checagens
 * das próprias páginas (is_admin).
 */

import { Outlet } from 'react-router-dom';

const RequireSubscription = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [userPlan, setUserPlan] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const checkSubscription = async () => {
      if (!currentUser) {
        if (isMounted) setIsChecking(false);
        return;
      }

      try {
        const response = await stripeService.getSubscriptionStatus();
        if (isMounted) {
          const plan = response?.data?.plan || 'free';
          const status = response?.data?.status || 'inactive';
          
          // Considerar como "ativo" se tem plano pago E status válido
          const isActive = plan !== 'free' && ['active', 'trialing'].includes(status);
          setUserPlan(isActive ? plan : 'free');
        }
      } catch (err) {
        console.warn('Erro ao verificar subscription:', err);
        if (isMounted) {
          setUserPlan('free');
        }
      } finally {
        if (isMounted) setIsChecking(false);
      }
    };

    checkSubscription();

    // Escutar evento de troca de conta (se usar AccountContext)
    const handleAccountSwitch = () => {
      setIsChecking(true);
      checkSubscription();
    };
    window.addEventListener('accountSwitched', handleAccountSwitch);

    return () => {
      isMounted = false;
      window.removeEventListener('accountSwitched', handleAccountSwitch);
    };
  }, [currentUser]);

  // Loading enquanto verifica
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Verificando assinatura...</p>
        </div>
      </div>
    );
  }

  // Usuário não logado → redirecionar para login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Modelo de créditos: Free entra no app (2 créditos/mês).
  // O gate de consumo é o saldo de créditos, aplicado pelo backend (403).
  return <Outlet />;
};

export default RequireSubscription;